/**
 * context/ThemeContext.js — Phase 3
 *
 * Compatibility with Phase 2 theme.js:
 *  ✓ Light mode palette is IDENTICAL to Phase 2 theme.js (same hex values)
 *  ✓ Custom shadows array preserved exactly
 *  ✓ shape.borderRadius: 12 preserved (was 10 in the first Phase 3 draft — fixed)
 *  ✓ MuiCard borderRadius: 14 preserved
 *  ✓ Typography (Nunito, weights, body2 fontSize) preserved
 *  ✓ MuiButton gradient + shadow overrides preserved
 *  ✓ MuiTab, MuiChip overrides preserved
 *  ✓ MainDashboard's useTheme() / useMediaQuery() works unchanged
 *
 * Phase 3 additions:
 *  + Dark mode palette (applied only when mode === 'dark')
 *  + HrmsThemeProvider replaces <ThemeProvider theme={theme}><CssBaseline />
 *  + useThemeMode() hook exposes { mode, toggleMode, setMode }
 *  + Persists to localStorage('hrms_theme'), reads OS preference as default
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';

const STORAGE_KEY = 'hrms_theme';
const ThemeContext = createContext({ mode: 'light', toggleMode: () => {}, setMode: () => {} });
export function useThemeMode() { return useContext(ThemeContext); }

// ── Shared shadow scale (preserved exactly from Phase 2 theme.js) ─────────────
const LIGHT_SHADOWS = [
  'none',
  '0px 1px 4px rgba(21,101,192,0.08)',
  '0px 2px 8px rgba(21,101,192,0.10)',
  '0px 4px 16px rgba(21,101,192,0.12)',
  '0px 6px 24px rgba(21,101,192,0.14)',
  ...Array(20).fill('0px 8px 32px rgba(21,101,192,0.16)'),
];

const DARK_SHADOWS = [
  'none',
  '0px 1px 4px rgba(0,0,0,0.3)',
  '0px 2px 8px rgba(0,0,0,0.35)',
  '0px 4px 16px rgba(0,0,0,0.4)',
  '0px 6px 24px rgba(0,0,0,0.45)',
  ...Array(20).fill('0px 8px 32px rgba(0,0,0,0.5)'),
];

// ── Theme factory ─────────────────────────────────────────────────────────────
function buildTheme(mode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main:         isDark ? '#42A5F5' : '#1565C0',
        light:        isDark ? '#90CAF9' : '#5E92F3',   // Phase 2: '#5E92F3'
        dark:         isDark ? '#1565C0' : '#003c8f',   // Phase 2: '#003c8f'
        contrastText: '#ffffff',
      },
      secondary: {
        main:  isDark ? '#4EBAAA' : '#00897B',          // Phase 2: '#00897B'
        light: isDark ? '#80CBC4' : '#4EBAAA',
        dark:  isDark ? '#00695C' : '#005B4F',
      },
      success: { main: '#2E7D32' },                     // Phase 2 unchanged
      warning: { main: '#F57F17' },
      error:   { main: '#C62828' },
      info:    { main: '#0277BD' },
      background: {
        default: isDark ? '#0F1117' : '#F0F4F8',        // Phase 2 light: '#F0F4F8'
        paper:   isDark ? '#1A1D27' : '#FFFFFF',
      },
      text: {
        primary:   isDark ? '#F0F2F8' : '#1A2332',      // Phase 2: '#1A2332'
        secondary: isDark ? '#9DA5B8' : '#546E7A',      // Phase 2: '#546E7A'
      },
    },

    typography: {
      // Phase 2 unchanged
      fontFamily: '"Nunito", "Segoe UI", sans-serif',
      h4:       { fontWeight: 700, letterSpacing: '-0.5px' },
      h5:       { fontWeight: 700 },
      h6:       { fontWeight: 600 },
      subtitle1:{ fontWeight: 600 },
      body2:    { fontSize: '0.8125rem' },
    },

    shape: { borderRadius: 12 },  // Phase 2: 12 (first draft had 10 — fixed)

    shadows: isDark ? DARK_SHADOWS : LIGHT_SHADOWS,

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9rem',
          },
          containedPrimary: isDark
            ? {
                background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)',
                boxShadow: '0 4px 14px rgba(66,165,245,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
                  boxShadow: '0 6px 20px rgba(66,165,245,0.45)',
                },
              }
            : {
                // Phase 2 exactly
                background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                boxShadow: '0 4px 14px rgba(21,101,192,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #003c8f 0%, #1565C0 100%)',
                  boxShadow: '0 6px 20px rgba(21,101,192,0.45)',
                },
              },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 14,                           // Phase 2: 14
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(21,101,192,0.08)'}`,
            boxShadow: isDark
              ? '0px 2px 12px rgba(0,0,0,0.3)'
              : '0px 2px 12px rgba(21,101,192,0.07)',   // Phase 2 exactly
          }),
        },
      },
      MuiChip: {
        // Phase 2 exactly
        styleOverrides: { root: { borderRadius: 6, fontWeight: 600 } },
      },
      MuiTab: {
        // Phase 2 exactly
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' } },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
    },
  });
}

// ── Provider ──────────────────────────────────────────────────────────────────
export function HrmsThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Apply data-theme on <html> for non-MUI CSS (scrollbars, etc.)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Sync with OS preference changes (only when user hasn't set a manual preference)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setModeState(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const muiTheme   = useMemo(() => buildTheme(mode), [mode]);
  const toggleMode = () => setModeState((m) => (m === 'dark' ? 'light' : 'dark'));
  const setMode    = (m) => setModeState(m);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
